import { Component, OnInit, OnDestroy, Inject, HostListener } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient, HttpDownloadProgressEvent, HttpEvent, HttpEventType } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { authorize } from 'src/app/helpers/AuthorizePage';
import { environment } from 'src/environments/environment';
import embed from 'vega-embed';
import { marked } from 'marked';
import { localStorageHelper } from 'src/app/helpers/localStorage';
import { SESSION_STORAGE_KEYS } from 'src/app/model/constants';
interface ChatMessage {
  content: string;
  contentInsight?: string;
  type: string;
  suggestions?: string[];
  request_id?: string;
  results?: any[];
  resultColumns?: string[];
  showTable?: boolean;
  chartId?: string;
  chartSpec?: any;
  sql?: string;
  showSql?: boolean;
  showInsight?: boolean;
  showChart?: boolean;
  showFeedback?: boolean;
  feedbackStatus?: 'idle' | 'submitting' | 'success' | 'error';
  submittedRating?: string;
  submittedText?: string;
  feedbackUpdated?: boolean;
  // contentInterpretation?: string;
  insightLoading?: boolean;
  thinking?: string;
  thinkingStatus?: string;
  isStreaming?: boolean;
  showThinking?: boolean;
  tools?: AgentTool[];
  showTools?: boolean;
  citations?: Citation[];
  relatedQueries?: RelatedQuery[];
  language?: string;
}

interface AgentTool {
  id?: string;
  name: string;
  type?: string;
  input?: any;
  result?: any;
  status?: string;
}

interface Citation {
  number: number;
  text: string;
}

interface RelatedQuery {
  question: string;
  answer: string;
  showAnswer?: boolean;
}

interface ParsedAgentResponse {
  text: string;
  thinking: string;
  tools: AgentTool[];
  citations: Citation[];
  relatedQueries: RelatedQuery[];
  suggestedQueries: string[];
  sql?: string;
  results?: any[];
  resultColumns?: string[];
  chartSpec?: any;
}

interface AgentStreamState {
  pending: string;
  processedLength: number;
  content: { [index: number]: any };
  finalResponse?: any;
  error?: any;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {
  messages: ChatMessage[] = [];
  conversationHistory: any[] = [];
  question: string = '';
  extendedThinking = false;
  cortexEndpoint = environment.chatbotAPI;
  cortexInsightEndpoint = environment.chatbotInsightAPI;
  feedbackEndpoint = environment.chatbotFeedbackApi;
  isLoading = false;
  userLetter = localStorage.getItem('username').toUpperCase().slice(0, 1);
  private chartInstances: { [key: string]: any } = {};
  private chartCounter = 0;
  private streamSub?: Subscription;
  private insightSub?: Subscription;
  private streamingMessage?: ChatMessage;

  feedbackOpen = false;
  feedbackMessage: ChatMessage | null = null;
  feedbackRating: string = '';
  feedbackText: string = '';
  activeCitation: Citation | null = null;

  constructor(private router: Router,
    @Inject(DOCUMENT) private _document: Document,
    private http: HttpClient,
    private sanitizer: DomSanitizer) { }

  ngOnInit() {
    if (!authorize()) {
      // TO-DO: Redirect to unauthorized page
      this.router.navigateByUrl('unauthorized');
    }
    this._document.body.classList.add('chatbot-page');
  }

  ngOnDestroy() {
    this.streamSub?.unsubscribe();
    this.insightSub?.unsubscribe();
    Object.keys(this.chartInstances).forEach(chartId => {
      this.chartInstances[chartId]?.finalize();
    });
    this.chartInstances = {};
    this._document.body.classList.remove('chatbot-page');
  }

  sendMessage(question = this.question) {
    const trimmedQuestion = question?.trim();
    if (!trimmedQuestion || this.isLoading) return;

    this.isLoading = true;
    (document.activeElement as HTMLElement)?.blur();

    const token = localStorage.getItem('accessToken');
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream'
    };

    this.messages.push({
      content: trimmedQuestion,
      type: 'user'
    });

    const body = {
      question: trimmedQuestion,
      messages: this.sanitizeConversationHistory(this.conversationHistory),
      use_chat_history: true,
      extended_thinking: this.extendedThinking,
      stream: true
    };

    const msg: ChatMessage = {
      content: '',
      type: 'bot',
      thinkingStatus: 'Thinking…',
      showThinking: true,
      isStreaming: true
    };
    const stream: AgentStreamState = {
      pending: '',
      processedLength: 0,
      content: {}
    };
    this.messages.push(msg);
    this.streamingMessage = msg;

    this.streamSub = this.http.post(this.cortexEndpoint, body, {
      headers,
      observe: 'events',
      reportProgress: true,
      responseType: 'text'
    }).subscribe(
      (event: HttpEvent<string>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const partialText = (event as HttpDownloadProgressEvent).partialText || '';
          const chunk = partialText.slice(stream.processedLength);
          stream.processedLength = partialText.length;
          const follow = this.isNearBottom();
          this.consumeAgentStream(chunk, stream, msg);
          if (follow) this.scrollToBottom('auto');
          return;
        }

        if (event.type === HttpEventType.Response) {
          if (this.isNearBottom()) this.scrollToBottom('auto');
          if (stream.processedLength === 0 && event.body) {
            this.consumeAgentStream(event.body, stream, msg);
          }
          this.flushAgentStream(stream, msg);
          this.finishAgentStream(stream, msg);
        }
      },
      (error: any) => {
        const responseError = this.parseHttpError(error?.error);
        msg.isStreaming = false;
        msg.thinkingStatus = '';
        this.removeEmptyStreamingMessage(msg);
        if (responseError?.suggestions === undefined) {
          this.handleLogout();
          this.messages.push({
            content: `Query returned all NULL values.`,
            type: 'bot'
          });

        }
        else if (responseError?.text || responseError?.suggestions) {
          const suggestions = Array.isArray(responseError?.suggestions)
            ? responseError.suggestions
            : [];
          const content = responseError?.text
            ? `${responseError.text}. Please refer to the recommended questions below :`
            : 'Please refer to the recommended questions below :';

          this.messages.push({
            content,
            type: 'bot',
            suggestions
          });
        } else {
          this.messages.push({
            content: responseError?.error || responseError?.message || 'An unexpected error occurred.',
            type: 'bot'
          });
        }

        this.isLoading = false;
      }
    );

    this.question = '';
    this.scrollToBottom('smooth');
  }

  private isNearBottom(): boolean {
    const view = this._document.defaultView;
    const el = this._document.documentElement;
    return !view || el.scrollHeight - (view.innerHeight + view.scrollY) < 80;
  }

  private scrollToBottom(behavior: ScrollBehavior) {
    setTimeout(() => {
      this._document.defaultView?.scrollTo({
        top: this._document.documentElement.scrollHeight,
        behavior
      });
    });
  }

  stopGeneration() {
    const msg = this.streamingMessage;
    this.streamSub?.unsubscribe();
    this.isLoading = false;
    if (!msg) return;

    msg.isStreaming = false;
    msg.thinkingStatus = '';
    if (msg.insightLoading) {
      this.insightSub?.unsubscribe();
      msg.insightLoading = false;
    }
    if (!msg.content && !msg.thinking && !msg.chartSpec && msg.results === undefined) {
      msg.content = 'Generation stopped.';
    }
  }

  sendSuggestedMessage(suggestion: string) {
    this.sendMessage(suggestion);
  }

  private consumeAgentStream(chunk: string, stream: AgentStreamState, message: ChatMessage) {
    if (!chunk) return;

    stream.pending += chunk;
    const events = stream.pending.split(/\r?\n\r?\n/);
    stream.pending = events.pop() || '';
    events.forEach(eventBlock => this.handleAgentStreamEvent(eventBlock, stream, message));
  }

  private flushAgentStream(stream: AgentStreamState, message: ChatMessage) {
    const pending = stream.pending.trim();
    stream.pending = '';
    if (pending) this.handleAgentStreamEvent(pending, stream, message);
  }

  private handleAgentStreamEvent(eventBlock: string, stream: AgentStreamState, message: ChatMessage) {
    let eventName = '';
    const dataLines: string[] = [];

    eventBlock.split(/\r?\n/).forEach(line => {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    });

    // Also accept a plain JSON response for backwards compatibility with a
    // proxy that buffers or does not return text/event-stream.
    const serializedData = dataLines.length > 0 ? dataLines.join('\n') : eventBlock.trim();
    if (!serializedData || serializedData === '[DONE]') return;

    let data: any;
    try {
      data = JSON.parse(serializedData);
    } catch (error) {
      console.warn('Ignoring an invalid agent stream event.', error);
      return;
    }

    if (!eventName && typeof data?.event === 'string') {
      eventName = data.event;
      data = data.data !== undefined ? data.data : data;
    }

    if (!eventName || eventName === 'response') {
      stream.finalResponse = data;
      this.applyAgentResponse(message, data);
      return;
    }

    if (eventName === 'language') {
      const language = typeof data?.language === 'string' ? data.language.trim() : '';
      if (language) {
        message.language = language;
      }
      return;
    }

    if (eventName === 'response.status') {
      message.thinkingStatus = data.message || this.formatAgentStatus(data.status);
      return;
    }

    if (eventName === 'response.text.delta' || eventName === 'response.thinking.delta') {
      const index = Number.isFinite(Number(data.content_index)) ? Number(data.content_index) : 0;
      const type = eventName === 'response.text.delta' ? 'text' : 'thinking';
      const block = stream.content[index]?.type === type
        ? stream.content[index]
        : type === 'text' ? { type, text: '' } : { type, thinking: { text: '' } };
      if (type === 'text') block.text += data.text || '';
      else block.thinking.text += data.text || '';
      stream.content[index] = block;
      this.applyStreamContent(message, stream);
      return;
    }

    if (eventName === 'response.text' || eventName === 'response.thinking') {
      const index = Number.isFinite(Number(data.content_index)) ? Number(data.content_index) : 0;
      stream.content[index] = eventName === 'response.text'
        ? { type: 'text', text: data.text || '' }
        : { type: 'thinking', thinking: { text: data.text || '', signature: data.signature } };
      this.applyStreamContent(message, stream);
      return;
    }

    const index = Number.isFinite(Number(data.content_index))
      ? Number(data.content_index)
      : Object.keys(stream.content).length;
    if (eventName === 'response.tool_use') {
      stream.content[index] = { type: 'tool_use', tool_use: data };
    } else if (eventName === 'response.tool_result') {
      stream.content[index] = { type: 'tool_result', tool_result: data };
    } else if (eventName === 'response.table') {
      stream.content[index] = { type: 'table', table: data };
    } else if (eventName === 'response.chart') {
      stream.content[index] = { type: 'chart', chart: data };
    } else if (eventName === 'response.suggested_queries') {
      stream.content[index] = {
        type: 'suggested_queries',
        suggested_queries: data.suggested_queries
      };
    } else if (eventName === 'response.tool_result.analyst.delta') {
      this.applyAnalystDelta(message, data.delta);
      return;
    } else if (eventName === 'error') {
      stream.error = data;
      message.content = data.message || 'An unexpected error occurred.';
      message.request_id = data.request_id;
      return;
    } else {
      return;
    }
    this.applyStreamContent(message, stream);
  }

  private applyStreamContent(message: ChatMessage, stream: AgentStreamState) {
    const content = Object.keys(stream.content)
      .map(Number)
      .sort((a, b) => a - b)
      .map(index => stream.content[index]);
    this.applyParsedAgentResponse(message, this.parseAgentResponse({ content }));
  }

  private applyAnalystDelta(message: ChatMessage, delta: any) {
    if (!delta) return;
    if (typeof delta.text === 'string') message.content = `${message.content || ''}${delta.text}`;
    if (typeof delta.think === 'string') message.thinking = `${message.thinking || ''}${delta.think}`;
    if (typeof delta.sql === 'string') message.sql = `${message.sql || ''}${delta.sql}`;
    if (delta.result_set) {
      const table = this.resultSetToTable(delta.result_set);
      if (table) {
        message.results = table.rows;
        message.resultColumns = table.columns;
        message.showTable = false;
        message.showInsight = true;
      }
    }
  }

  private finishAgentStream(stream: AgentStreamState, message: ChatMessage) {
    message.isStreaming = false;
    message.thinkingStatus = '';
    this.isLoading = false;

    if (stream.error) return;
    this.prepareStructuredMessage(message);
    if (stream.finalResponse) {
      const response = stream.finalResponse;
      if (Array.isArray(response.messages)) {
        this.conversationHistory = response.messages;
      } else if (response.role === 'assistant' && Array.isArray(response.content)) {
        this.conversationHistory = [...this.conversationHistory, response];
      }
      return;
    }

    if (!message.content && !message.thinking && !message.chartSpec && message.results === undefined &&
      !message.suggestions?.length) {
      message.content = 'No response from server.';
    }
  }

  private applyAgentResponse(message: ChatMessage, response: any) {
    const agentResponse = this.parseAgentResponse(response);
    this.applyParsedAgentResponse(message, agentResponse);
    const hasStructuredResponse = !!agentResponse.chartSpec || agentResponse.results !== undefined ||
      agentResponse.relatedQueries.length > 0 || agentResponse.suggestedQueries.length > 0;
    const legacyText = agentResponse.relatedQueries.length > 0 ? '' : response.natural_response;
    message.content = agentResponse.text || legacyText || response.error ||
      (hasStructuredResponse ? '' : message.content);
    message.results = agentResponse.results !== undefined
      ? agentResponse.results
      : (Array.isArray(response.results) ? response.results : message.results);
    message.resultColumns = agentResponse.resultColumns || message.resultColumns;
    message.sql = agentResponse.sql || response.sql || message.sql;
    message.request_id = response.request_id || response.metadata?.run_id || message.request_id;
    this.prepareStructuredMessage(message);
  }

  private applyParsedAgentResponse(message: ChatMessage, response: ParsedAgentResponse) {
    if (response.text) message.content = response.text;
    if (response.thinking) message.thinking = response.thinking;
    if (response.tools.length > 0) message.tools = response.tools;
    if (response.citations.length > 0) message.citations = response.citations;
    if (response.relatedQueries.length > 0) message.relatedQueries = response.relatedQueries;
    if (response.suggestedQueries.length > 0) message.suggestions = response.suggestedQueries;
    if (response.sql) message.sql = response.sql;
    if (response.results !== undefined) message.results = response.results;
    if (response.resultColumns) message.resultColumns = response.resultColumns;
    if (response.chartSpec) message.chartSpec = response.chartSpec;
    this.prepareStructuredMessage(message);
  }

  private prepareStructuredMessage(message: ChatMessage) {
    if (message.results !== undefined) {
      message.resultColumns = message.resultColumns?.length
        ? message.resultColumns
        : this.getColumnsFromRows(message.results);
      if (message.showTable === undefined) message.showTable = false;
      if (!message.contentInsight && !message.insightLoading) {
        this.loadInsight(message);
      }
    } else {
      message.showInsight = false;
    }
    if (message.chartSpec && !message.chartId) {
      message.chartId = `chat-chart-${this.chartCounter++}`;
      message.showChart = false;
    }
    if (message.sql && message.showSql === undefined) message.showSql = false;
  }

  private formatAgentStatus(status: string): string {
    if (!status) return 'Thinking…';
    return status.replace(/_/g, ' ').replace(/^\w/, character => character.toUpperCase());
  }

  private removeEmptyStreamingMessage(message: ChatMessage) {
    if (!message.content && !message.thinking && !message.chartSpec && message.results === undefined) {
      const index = this.messages.indexOf(message);
      if (index >= 0) this.messages.splice(index, 1);
    }
  }

  private parseHttpError(error: any): any {
    if (typeof error !== 'string') return error || {};
    try {
      return JSON.parse(error);
    } catch (_) {
      return { message: error };
    }
  }

  toggleInsight(message: ChatMessage) {
    if (!message.contentInsight || message.insightLoading) return;
    message.showInsight = !message.showInsight;
  }

  private loadInsight(message: ChatMessage) {
    const token = localStorage.getItem('accessToken');
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const body = {
      sql_query: message.sql,
      results: message.results,
      language: message.language,
    };

    message.insightLoading = true;

    this.insightSub = this.http.post(this.cortexInsightEndpoint, body, { headers }).subscribe(
      (response: any) => {
        message.showInsight = true;
        message.contentInsight = response?.insight || 'Data may not exist for insight.';
        message.insightLoading = false;
      },
      (error: any) => {
        message.showInsight = true;
        message.contentInsight = 'Data may not exist for insight.';
        message.insightLoading = false;
      }
    );
  }

  private sanitizeConversationHistory(history: any[] = []): any[] {
    const normalized = history
      .map((msg: any) => {
        if (!msg || !msg.role || !Array.isArray(msg.content)) return null;

        const role = msg.role;
        if (role !== 'user' && role !== 'assistant') return null;

        const textContent = msg.content
          .filter((item: any) => item?.type === 'text' && typeof item?.text === 'string')
          .map((item: any) => ({
            type: 'text',
            text: item.text.trim()
          }))
          .filter((item: any) => item.text.length > 0);

        if (textContent.length === 0) return null;

        return { role, content: textContent };
      })
      .filter((msg: any) => !!msg);

    const deduped: any[] = [];
    for (const msg of normalized) {
      const prev = deduped[deduped.length - 1];
      if (!prev || prev.role !== msg.role) {
        deduped.push(msg);
      }
    }

    if (deduped.length > 0 && deduped[deduped.length - 1].role === 'user') {
      deduped.pop();
    }

    return deduped;
  }

  hasThinking(message: ChatMessage): boolean {
    return message?.isStreaming === true ||
      (typeof message?.thinking === 'string' && message.thinking.trim().length > 0) ||
      (typeof message?.thinkingStatus === 'string' && message.thinkingStatus.trim().length > 0);
  }

  hasTools(message: ChatMessage): boolean {
    return Array.isArray(message?.tools) && message.tools.some(tool => !!tool && !!(tool.name || tool.type));
  }

  hasTable(message: ChatMessage): boolean {
    return Array.isArray(message?.results);
  }

  formatTableCell(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (_) {
        return String(value);
      }
    }
    return String(value);
  }

  openCitation(citation: Citation) {
    this.activeCitation = citation;
  }

  closeCitation() {
    this.activeCitation = null;
  }

  citationColorIndex(number: number): number {
    return ((Math.max(number, 1) - 1) % 7) + 1;
  }

  /** Maps Cortex Agents content blocks to presentation data. Analyst fields are
   * intentionally handled by the caller as a backwards-compatible fallback. */
  private parseAgentResponse(response: any): ParsedAgentResponse {
    const lastAssistantMessage = Array.isArray(response?.messages)
      ? [...response.messages].reverse().find((message: any) => message?.role === 'assistant')
      : undefined;
    const content = Array.isArray(response?.content)
      ? response.content
      : Array.isArray(response?.message?.content) ? response.message.content
        : Array.isArray(lastAssistantMessage?.content) ? lastAssistantMessage.content : [];
    const text: string[] = [];
    const thinking: string[] = [];
    const tools: AgentTool[] = [];
    const citations: Citation[] = [];
    const suggestedQueries: string[] = [];
    const toolById: { [id: string]: AgentTool } = {};
    let sql: string | undefined;
    let results: any[] | undefined;
    let resultColumns: string[] | undefined;
    let chartSpec: any | undefined;

    content.forEach((block: any) => {
      if (block?.type === 'text' && typeof block.text === 'string') {
        text.push(block.text);
        return;
      }
      if (block?.type === 'thinking' && typeof block.thinking?.text === 'string') {
        thinking.push(block.thinking.text);
        return;
      }
      if (block?.type === 'chart' && block.chart?.chart_spec) {
        chartSpec = this.parseChartSpec(block.chart.chart_spec);
        return;
      }
      if (block?.type === 'suggested_queries') {
        this.extractSuggestedQueries(block.suggested_queries || block.queries || block)
          .forEach(query => {
            if (!suggestedQueries.includes(query)) suggestedQueries.push(query);
          });
        return;
      }
      if (block?.type === 'fetched_table' && typeof block.tableMarkdown === 'string') {
        const table = this.markdownTableToRows(block.tableMarkdown);
        if (table) {
          results = table.rows;
          resultColumns = table.columns;
        }
        return;
      }
      if (block?.type === 'table' && block.table) {
        const table = this.directTableToRows(block.table);
        if (table) {
          results = table.rows;
          resultColumns = table.columns;
        }
        return;
      }
      if (block?.type === 'tool_use' && block.tool_use) {
        const toolUse = block.tool_use;
        const tool: AgentTool = {
          id: toolUse.tool_use_id,
          name: toolUse.name || 'Unnamed tool',
          type: toolUse.type,
          input: toolUse.input
        };
        tools.push(tool);
        if (tool.id) toolById[tool.id] = tool;
        if (typeof toolUse.input?.sql === 'string') sql = toolUse.input.sql;
        return;
      }
      if ((block?.type === 'tool_result' || block?.type === 'tool_results') && (block.tool_result || block.tool_results)) {
        const toolResult = block.tool_result || block.tool_results;
        const tool: AgentTool = toolById[toolResult.tool_use_id] || {
          id: toolResult.tool_use_id,
          name: toolResult.name || 'Unnamed tool',
          type: toolResult.type
        };
        if (!toolById[toolResult.tool_use_id]) tools.push(tool);
        tool.status = toolResult.status;
        tool.result = toolResult.content;
        (toolResult.content || []).forEach((item: any) => {
          const data = item?.json;
          if (!data) return;
          if (typeof data.text === 'string') text.push(data.text);
          if (typeof data.sql === 'string') sql = data.sql;
          if (Array.isArray(data.searchResults)) {
            data.searchResults.forEach((result: any) => {
              const number = Number(result?.source_id);
              if (!Number.isFinite(number) || typeof result?.text !== 'string') return;
              if (!citations.some(citation => citation.number === number)) {
                citations.push({ number, text: result.text });
              }
            });
          }
          if (data.result_set) {
            const table = this.resultSetToTable(data.result_set);
            results = table?.rows;
            resultColumns = table?.columns;
          }
        });
      }
    });

    const combinedText = text.join('\n\n').trim();
    const relatedQueries = this.extractRelatedQueries(combinedText);
    const displayText = this.removeRelatedQueries(combinedText).trim();
    const referencedCitationNumbers = new Set<number>();
    const citationPattern = /【†(\d+)†】/g;
    let citationMatch: RegExpExecArray | null;
    while ((citationMatch = citationPattern.exec(combinedText)) !== null) {
      referencedCitationNumbers.add(Number(citationMatch[1]));
    }

    return {
      text: displayText,
      thinking: thinking.join('\n\n').trim(),
      tools,
      citations: citations.filter(citation => referencedCitationNumbers.has(citation.number)),
      relatedQueries,
      suggestedQueries,
      sql,
      results,
      resultColumns,
      chartSpec
    };
  }

  private extractSuggestedQueries(value: any): string[] {
    const queries = Array.isArray(value)
      ? value
      : Array.isArray(value?.suggested_queries) ? value.suggested_queries
        : Array.isArray(value?.queries) ? value.queries : [];

    return queries
      .map((item: any) => typeof item === 'string' ? item : item?.query)
      .filter((query: any) => typeof query === 'string' && query.trim().length > 0)
      .map((query: string) => query.trim());
  }

  private extractRelatedQueries(text: string): RelatedQuery[] {
    const pattern = /Related query:\s*(.*?)\s*Answer:\s*(.*?)(?=\nRelated query:|\n*$)/gis;
    const relatedQueries: RelatedQuery[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      const question = match[1].trim();
      if (question.length > 0) {
        relatedQueries.push({
          question,
          answer: match[2].trim(),
          showAnswer: false
        });
      }
    }
    return relatedQueries;
  }

  private removeRelatedQueries(text: string): string {
    return text.replace(/Related query:\s*(.*?)\s*Answer:\s*(.*?)(?=\nRelated query:|\n*$)/gis, '');
  }

  private parseChartSpec(chartSpec: string | object): any | undefined {
    try {
      const parsed = typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch (error) {
      console.error('Unable to parse Vega-Lite chart specification.', error);
      return undefined;
    }
  }

  private resultSetToTable(resultSet: any): { rows: any[]; columns: string[] } | undefined {
    const columnDefinitions = resultSet?.resultSetMetaData?.rowType || resultSet?.metadata?.rowType || resultSet?.columns || [];
    const sourceRows = resultSet?.data || resultSet?.rows;
    if (!Array.isArray(sourceRows)) return undefined;

    const metadataColumns = Array.isArray(columnDefinitions)
      ? columnDefinitions
        .map((column: any) => typeof column === 'string' ? column : column?.name)
        .filter((column: any) => typeof column === 'string' && column.length > 0)
      : [];

    const rows = sourceRows.map((row: any) => {
      if (!Array.isArray(row)) {
        return row && typeof row === 'object' ? row : { Value: row };
      }

      const columns = metadataColumns.length > 0
        ? metadataColumns
        : row.map((_: any, index: number) => `Column ${index + 1}`);
      return columns.reduce((mapped: any, column: string, index: number) => {
        mapped[column] = row[index];
        return mapped;
      }, {});
    });

    return {
      rows,
      columns: metadataColumns.length > 0 ? metadataColumns : this.getColumnsFromRows(rows)
    };
  }

  private directTableToRows(tableData: any): { rows: any[]; columns: string[] } | undefined {
    if (typeof tableData === 'string') return this.markdownTableToRows(tableData);
    if (tableData?.result_set) return this.resultSetToTable(tableData.result_set);
    if (Array.isArray(tableData?.data) || Array.isArray(tableData?.rows)) {
      return this.resultSetToTable(tableData);
    }
    if (Array.isArray(tableData)) {
      return this.resultSetToTable({ data: tableData });
    }
    return undefined;
  }

  private markdownTableToRows(markdown: string): { rows: any[]; columns: string[] } | undefined {
    const lines = markdown
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line.includes('|'));
    if (lines.length < 2) return undefined;

    const columns = this.splitMarkdownTableRow(lines[0]);
    const separator = this.splitMarkdownTableRow(lines[1]);
    if (columns.length === 0 || !separator.every(cell => /^:?-{3,}:?$/.test(cell))) return undefined;

    const rows = lines.slice(2).map(line => {
      const values = this.splitMarkdownTableRow(line);
      return columns.reduce((row: any, column: string, index: number) => {
        row[column] = values[index] || '';
        return row;
      }, {});
    });

    return { rows, columns };
  }

  private splitMarkdownTableRow(line: string): string[] {
    const trimmed = line.replace(/^\|/, '').replace(/\|$/, '');
    const cells: string[] = [];
    let cell = '';
    let escaped = false;

    for (const character of trimmed) {
      if (escaped) {
        cell += character;
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === '|') {
        cells.push(cell.trim());
        cell = '';
      } else {
        cell += character;
      }
    }
    cells.push(cell.trim());
    return cells;
  }

  private getColumnsFromRows(rows: any[]): string[] {
    const columns: string[] = [];
    rows.forEach(row => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return;
      Object.keys(row).forEach(column => {
        if (!columns.includes(column)) columns.push(column);
      });
    });
    return columns;
  }
  toggleChart(message: ChatMessage) {
    message.showChart = !message.showChart;
    if (message.showChart) {
      setTimeout(() => this.renderChart(message), 0);
    }
  }

  private async renderChart(message: ChatMessage) {
    const { chartId, chartSpec } = message;
    if (!chartId || !chartSpec) return;

    this.chartInstances[chartId]?.finalize();
    delete this.chartInstances[chartId];

    const el = this._document.getElementById(chartId);
    if (!el) return;

    const containerWidth = Math.floor(el.offsetWidth - 34); // Adjust for padding/margin
    const containerHeight = Math.floor(containerWidth * 3 / 4); // Maintain a 4:3 aspect ratio

    const fixedWidthChartSpec = {
      ...chartSpec,
      width: containerWidth,
      height: containerHeight,
      autosize: {
        type: 'fit',
        contains: 'padding',
        resize: false
      }
    };

    try {
      const result = await embed(el, fixedWidthChartSpec, {
        // https://vega.github.io/vega-lite/docs/legend.html#config
        // https://vega.github.io/vega-lite/docs/config.html#legend-config
        actions: true,
        renderer: 'canvas',
        width: containerWidth,
        height: containerHeight,
        config: {
          "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
          "background": "transparent",
          "fieldTitle": "verbal",
          "autosize": {
            "type": "fit",
            "contains": "padding",
            "resize": false
          },
          "numberFormat": ",.2~f",
          "timeFormat": "%b %d, %Y",
          "tooltipFormat": {
            "numberFormat": ",.4~f",
            "timeFormat": "%b %d, %Y %H:%M"
          },
          "title": {
            "align": "left",
            "anchor": "start",
            "color": "#5d6a85",
            "subtitleFontStyle": "normal",
            "fontWeight": 600,
            "fontSize": 16,
            "orient": "top",
            "offset": 26
          },
          "header": {
            "titleFontWeight": 400,
            "titleFontSize": 16,
            "titleColor": "#5d6a85",
            "titleFontStyle": "normal",
            "labelFontSize": 12,
            "labelFontWeight": 400,
            "labelColor": "#5d6a85",
            "labelFontStyle": "normal"
          },
          "axis": {
            "labelFontSize": 10,
            "labelFontWeight": 400,
            "labelColor": "#5d6a85",
            "labelFontStyle": "normal",
            "titleFontWeight": 400,
            "titleFontSize": 12,
            "titleColor": "#5d6a85",
            "titleFontStyle": "normal",
            "ticks": false,
            "gridColor": "#d5dae4",
            "domain": false,
            "domainWidth": 1,
            "domainColor": "#d5dae4",
            "labelFlush": false,
            "labelBound": false,
            "labelLimit": 100,
            "titlePadding": 16,
            "labelPadding": 12,
            "labelSeparation": 4,
            "labelOverlap": true
          },
          "axisDiscrete": {
            "grid": false
          },
          "axisXPoint": {
            "grid": false
          },
          "axisTemporal": {
            "grid": false
          },
          "axisXBand": {
            "grid": false
          },
          "range": {
            "category": [
              "#266ce0",
              "#f4be5d",
              "#04c097",
              "#ca3d2e",
              "#7b58e2",
              "#f69247",
              "#67d6ff",
              "#a944c0"
            ],
            "heatmap": [
              "#e4eeff",
              "#c6dcff",
              "#a0c5ff",
              "#74a6f8",
              "#5b98ff",
              "#3782ff",
              "#266ce0",
              "#2264d0",
              "#234f99",
              "#223f6f"
            ],
            "diverging": [
              "#234f99",
              "#266ce0",
              "#5b98ff",
              "#a0c5ff",
              "#e4eeff",
              "#fafafa",
              "#ffe8e4",
              "#ffb4a7",
              "#fa6c59",
              "#ca3d2e",
              "#8e3025"
            ]
          },
          "view": {
            "strokeWidth": 0,
            "stroke": "transparent",
            "continuousHeight": 150,
            "continuousWidth": 400
          },
          "concat": {
            "columns": 1
          },
          "facet": {
            "columns": 1
          },
          "scale": {
            "bandWithNestedOffsetPaddingInner": 0.2,
            "barBandPaddingInner": 0.1,
            "offsetBandPaddingInner": 0.1,
            "offsetBandPaddingOuter": 0
          },
          "bar": {
            "binSpacing": 1,
            "discreteBandSize": {
              "band": 0.9
            },
            "continuousBandSize": 5,
            "timeUnitBandPosition": 0.5,
            "timeUnitBandSize": 1,
            "minBandSize": 5
          },
          "area": {
            "opacity": 0.8
          },
          "rect": {
            "cornerRadius": 0
          },
          "arc": {
            "padAngle": 0.01,
            "innerRadius": 20
          },
          "line": {
            "strokeWidth": 2,
            "strokeCap": "round",
            "strokeJoin": "round"
          },
          "trail": {
            "strokeWidth": 2
          },
          "point": {
            "size": 50
          },
          "circle": {
            "size": 50
          },
          "square": {
            "size": 50
          },
          "tick": {
            "thickness": 1,
            "bandSize": 14
          },
          "rule": {
            "stroke": "#d5dae4"
          },
          "text": {
            "color": "#5d6a85",
            "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
            "fontSize": 12,
            "fontWeight": 400
          },
          "geoshape": {
            "stroke": "#d5dae4",
            "strokeWidth": 0.5
          },
          "selection": {
            "interval": {
              "mark": {
                "stroke": "#bdc4d5",
                "strokeWidth": 1,
                // "cornerRadius": 2,
                "fillOpacity": 0.1,
                "fill": "#bdc4d5"
              }
            }
          },
          "legend": {
            "orient": "top",
            "direction": "horizontal",
            "labelColor": "#5d6a85",
            "labelFontSize": 10,
            "labelFontWeight": 400,
            "labelFontStyle": "normal",
            "titleColor": "#5d6a85",
            "titleFontSize": 12,
            "titleFontWeight": 700,
            "titleFontStyle": "normal",
            "symbolSize": 80,
            "padding": 0,
            "offset": 100
          },
          "mark": {
            "tooltip": true,
            "color": "#266ce0"
          },
          "style": {
            "guide-label": {
              "fill": "#5d6a85",
              "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
              "fontWeight": 400
            },
            "guide-title": {
              "fill": "#5d6a85",
              "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
              "fontWeight": 400
            },
            "group-title": {
              "fill": "#5d6a85",
              "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
              "fontWeight": 600
            },
            "group-subtitle": {
              "fill": "#5d6a85",
              "font": "Inter, Roboto, \"Helvetica Neue\", \"Arial Nova\", \"Nimbus Sans\", Arial, sans-serif",
              "fontWeight": 400
            }
          }
        }
      });

      // console.log('Vega-Lite chart rendered successfully:', result);

      this.chartInstances[chartId] = result;
    } catch (error) {
      el.textContent = 'Unable to render this visualization.';
      console.error('Unable to render Vega-Lite chart.', error);
    }
  }

  toggleFeedback(message: ChatMessage) {
    const opening = !message.showFeedback;
    this.messages.forEach(m => m.showFeedback = false);
    if (opening) {
      this.feedbackMessage = message;
      this.feedbackRating = message.submittedRating || '';
      this.feedbackText = message.submittedText || '';
      message.showFeedback = true;
    } else {
      this.feedbackMessage = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.feedbackMessage) return;
    // composedPath survives *ngIf removing the clicked node
    const inside = event.composedPath().some(el => (el as HTMLElement).classList?.contains('feedback-btn-wrapper'));
    if (!inside) this.closeFeedback();
  }

  closeFeedback() {
    this.messages.forEach(m => m.showFeedback = false);
    this.feedbackMessage = null;
  }

  isFeedbackChanged(message: ChatMessage) {
    return this.feedbackRating !== (message.submittedRating || '')
      || this.feedbackText.trim() !== (message.submittedText || '');
  }

  canSubmitFeedback(message: ChatMessage) {
    return !!this.feedbackRating && message.feedbackStatus !== 'submitting' && this.isFeedbackChanged(message);
  }

  submitFeedback() {
    if (!this.feedbackMessage || !this.canSubmitFeedback(this.feedbackMessage)) return;

    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };

    const body: any = {
      request_id: this.generateGuid(),
      positive: this.feedbackRating === 'good'
    };

    if (this.feedbackText && this.feedbackText.trim()) {
      body.feedback_message = this.feedbackText.trim();
    }

    const submittedMessage = this.feedbackMessage;
    submittedMessage.feedbackStatus = 'submitting';

    this.http.post<{ success: boolean }>(this.feedbackEndpoint, body, { headers }).subscribe(
      (response) => {
        if (response && response.success) {
          submittedMessage.feedbackStatus = 'success';
          submittedMessage.feedbackUpdated = !!submittedMessage.submittedRating;
          submittedMessage.submittedRating = body.positive ? 'good' : 'bad';
          submittedMessage.submittedText = body.feedback_message || '';
          setTimeout(() => {
            submittedMessage.showFeedback = false;
            submittedMessage.feedbackStatus = 'idle';
            if (this.feedbackMessage === submittedMessage) {
              this.feedbackMessage = null;
            }
          }, 2000);
        } else {
          submittedMessage.feedbackStatus = 'error';
        }
      },
      (error: any) => {
        console.error('Failed to submit feedback:', error);
        submittedMessage.feedbackStatus = 'error';
      }
    );
  }

  parseMarkdown(text: string): SafeHtml {
    if (!text) return '';
    const textWithCitationMarkers = text.replace(/【†(\d+)†】/g, (_match, number) => {
      const colorIndex = this.citationColorIndex(Number(number));
      return `<span class="citation-marker citation-color-${colorIndex}">${number}</span>`;
    });
    const html = marked.parse(textWithCitationMarkers) as string;
    const htmlWithStyledTables = html
      .replace(/<table>/g, '<div class="markdown-table-wrapper"><table class="results-table">')
      .replace(/<\/table>/g, '</table></div>');
    return this.sanitizer.bypassSecurityTrustHtml(htmlWithStyledTables);
  }

  highlightSql(sql: string): SafeHtml {
    if (!sql) return '';

    const escape = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const keywords = new Set([
      'SELECT', 'FROM', 'WHERE', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'FULL', 'CROSS',
      'ON', 'GROUP', 'BY', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET', 'INSERT', 'INTO', 'VALUES',
      'UPDATE', 'SET', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'TABLE', 'INDEX', 'VIEW', 'AS',
      'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'BETWEEN', 'LIKE', 'ILIKE', 'DISTINCT', 'ALL',
      'UNION', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'OVER', 'PARTITION', 'ROWS',
      'RANGE', 'PRECEDING', 'FOLLOWING', 'UNBOUNDED', 'RECURSIVE', 'EXISTS', 'ANY', 'EXCEPT',
      'INTERSECT', 'REPLACE', 'MERGE', 'TRUNCATE', 'BEGIN', 'COMMIT', 'ROLLBACK', 'ASC',
      'DESC', 'TOP', 'FETCH', 'NEXT', 'ONLY', 'TRUE', 'FALSE', 'QUALIFY', 'CURRENT', 'ROW',
      'NATURAL', 'USING', 'LATERAL', 'APPLY', 'TABLESAMPLE', 'SAMPLE', 'PIVOT', 'UNPIVOT'
    ]);

    const functions = new Set([
      'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COALESCE', 'NULLIF', 'IFF', 'IFNULL', 'NVL',
      'CONCAT', 'SUBSTR', 'SUBSTRING', 'TRIM', 'LTRIM', 'RTRIM', 'UPPER', 'LOWER', 'LENGTH',
      'LEN', 'CHARINDEX', 'PATINDEX', 'ROUND', 'FLOOR', 'CEIL', 'CEILING', 'ABS', 'POWER',
      'SQRT', 'MOD', 'TRUNC', 'DATEADD', 'DATEDIFF', 'DATENAME', 'DATEPART', 'GETDATE', 'NOW',
      'SYSDATE', 'TO_DATE', 'TO_CHAR', 'TO_NUMBER', 'TO_TIMESTAMP', 'DATE_TRUNC', 'DATE_PART',
      'YEAR', 'MONTH', 'DAY', 'HOUR', 'MINUTE', 'SECOND', 'QUARTER', 'ROW_NUMBER', 'RANK',
      'DENSE_RANK', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'NTH_VALUE', 'NTILE',
      'PERCENT_RANK', 'CUME_DIST', 'CAST', 'CONVERT', 'TRY_CAST', 'ISNULL', 'ISNUMERIC',
      'STRING_AGG', 'LISTAGG', 'GROUP_CONCAT', 'ARRAY_AGG', 'JSON_AGG', 'ZEROIFNULL',
      'DECODE', 'GREATEST', 'LEAST', 'LPAD', 'RPAD', 'SPLIT_PART', 'REGEXP_REPLACE',
      'REGEXP_SUBSTR', 'CONTAINS', 'STARTSWITH', 'ENDSWITH', 'TRY_TO_NUMBER', 'TRY_TO_DATE'
    ]);

    // Single-pass tokenizer: strings → comments → numbers → identifiers/keywords → others
    const tokenRe = /('(?:''|[^'])*')|("(?:[^"]|\\")*")|(`[^`]*`)|(--[^\n]*)|(\/\*[\s\S]*?\*\/)|\b(\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b|([A-Za-z_]\w*)|([^\s\w])/g;

    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRe.exec(sql)) !== null) {
      if (match.index > lastIndex) {
        result += escape(sql.slice(lastIndex, match.index));
      }
      lastIndex = tokenRe.lastIndex;

      const [full, sqStr, dqStr, btStr, lineComment, blockComment, number, ident] = match;

      if (sqStr !== undefined) {
        result += `<span class="sql-string">${escape(sqStr)}</span>`;
      } else if (dqStr !== undefined || btStr !== undefined) {
        result += `<span class="sql-identifier">${escape(full)}</span>`;
      } else if (lineComment !== undefined || blockComment !== undefined) {
        result += `<span class="sql-comment">${escape(full)}</span>`;
      } else if (number !== undefined) {
        result += `<span class="sql-number">${escape(full)}</span>`;
      } else if (ident !== undefined) {
        const upper = ident.toUpperCase();
        if (functions.has(upper)) {
          result += `<span class="sql-function">${escape(ident)}</span>`;
        } else if (keywords.has(upper)) {
          result += `<span class="sql-keyword">${escape(ident)}</span>`;
        } else {
          result += escape(ident);
        }
      } else {
        result += escape(full);
      }
    }

    if (lastIndex < sql.length) {
      result += escape(sql.slice(lastIndex));
    }

    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  private generateGuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private clearStorage() {
    localStorageHelper.clear();
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_URL);
  }

  public handleLogout() {
    this.clearStorage();
    location.href = 'authentication/login';
  }

}
